import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CreateUserRequest,
    ListSelectableUsersParams,
    ListUsersParams,
    SetUserOtpRequest,
    UpdateUserRequest,
} from '@/lib/api/user-api'
import { m } from '@/lib/paraglide/messages'
import { userApi } from '@/lib/api/user-api'
import { userErrorToast } from '@/lib/user-error-toast'

export const userKeys = {
    all: ['users'] as const,
    lists: () => [...userKeys.all, 'list'] as const,
    list: (params: ListUsersParams) => [...userKeys.lists(), params] as const,
    detail: (id: string) => [...userKeys.all, 'detail', id] as const,
    roles: () => ['roles'] as const,
    displays: () => [...userKeys.all, 'displays'] as const,
    selectable: () => [...userKeys.all, 'selectable'] as const,
    orderCreators: () => [...userKeys.all, 'order-creators'] as const,
}

export function useDisplayUsers() {
    return useQuery({
        queryKey: userKeys.displays(),
        queryFn: async () => {
            const res = await userApi.listDisplays()
            return res.data ?? []
        },
    })
}

// All active users for people-pickers (care-log implementer selector).
export function useSelectableUsers() {
    return useQuery({
        queryKey: userKeys.selectable(),
        queryFn: async () => {
            const res = await userApi.listSelectable()
            return res.data ?? []
        },
    })
}

/**
 * Người dùng chọn được (không cần users.read) dạng tìm trên server + tải thêm
 * khi cuộn (M-143, bộ lọc Người tạo của Báo cáo thu chi).
 */
export function useSelectableUsersInfinite(
    params: Omit<ListSelectableUsersParams, 'page' | 'ids'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [...userKeys.selectable(), 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            userApi.listSelectablePage({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

/** Tên của các người đã chọn (id trên URL) — chip đọc được khi tải lại trang. */
export function useSelectableUsersByIds(ids: string[]) {
    const key = ids.join(',')
    return useQuery({
        enabled: ids.length > 0,
        queryKey: [...userKeys.selectable(), 'by-ids', key] as const,
        queryFn: async () => {
            const res = await userApi.listSelectablePage({
                ids: key,
                per_page: ids.length,
            })
            return res.data ?? []
        },
        staleTime: 5 * 60_000,
    })
}

// Người tạo đơn cho bộ lọc xuất Excel: đang hoạt động + đã nghỉ có tạo đơn.
export function useOrderCreators(options?: { enabled?: boolean }) {
    return useQuery({
        queryKey: userKeys.orderCreators(),
        queryFn: async () => {
            const res = await userApi.listOrderCreators()
            return res.data ?? []
        },
        enabled: options?.enabled ?? true,
    })
}

export function useUsers(
    params?: ListUsersParams,
    options?: { enabled?: boolean },
) {
    return useQuery({
        queryKey: userKeys.list(params ?? {}),
        queryFn: async () => {
            const res = await userApi.list(params)
            return { data: res.data ?? [], meta: res.meta }
        },
        enabled: options?.enabled,
    })
}

/**
 * Danh sách người dùng dạng load-more cho mobile (desktop vẫn dùng `useUsers`
 * với nút Trước/Sau). Bỏ `page` khỏi params — hook tự quản trang.
 */
export function useUsersInfinite(
    params: Omit<ListUsersParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [...userKeys.lists(), 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            userApi.list({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function useRoles() {
    return useQuery({
        queryKey: userKeys.roles(),
        queryFn: async () => {
            const res = await userApi.listRoles()
            return res.data ?? []
        },
    })
}

export function useCreateUser() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: CreateUserRequest) => userApi.create(data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: userKeys.lists() })
            toast.success(m.toast_user_create_success())
        },
        onError: (err) => {
            const { title, description } = userErrorToast(
                err,
                m.toast_user_create_error(),
            )
            toast.error(title, { description })
        },
    })
}

export function useUpdateUser(id: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: UpdateUserRequest) => userApi.update(id, data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: userKeys.lists() })
            toast.success(m.toast_user_update_success())
        },
        onError: (err) => {
            const { title, description } = userErrorToast(
                err,
                m.toast_user_update_error(),
            )
            toast.error(title, { description })
        },
    })
}

export function useAssignRoles(id: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (roleIds: string[]) => userApi.assignRoles(id, roleIds),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: userKeys.lists() })
            toast.success(m.toast_user_role_success())
        },
        onError: (err) => {
            const { title, description } = userErrorToast(
                err,
                m.toast_user_role_error(),
            )
            toast.error(title, { description })
        },
    })
}

export function useChangePassword(id: string) {
    return useMutation({
        mutationFn: (data: { old_password?: string; new_password: string }) =>
            userApi.changePassword(id, data),
        onSuccess: () => toast.success(m.toast_user_password_success()),
        onError: (err) => {
            const { title, description } = userErrorToast(
                err,
                m.toast_user_password_error(),
            )
            toast.error(title, { description })
        },
    })
}

export function useSetUserOtp(id: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: SetUserOtpRequest) => userApi.setOtp(id, data),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: userKeys.lists() })
            toast.success(m.toast_user_otp_success())
        },
        onError: (err) => {
            const { title, description } = userErrorToast(
                err,
                m.toast_user_otp_error(),
            )
            toast.error(title, { description })
        },
    })
}
